import React, { useState } from "react";
import { USERNAME_STYLES, AVATAR_FRAMES, BANNER_BADGES } from "../constants/loadout";
import { Link } from "react-router-dom";
import MemberHoverCard from "./MemberHoverCard";
import "./VipLoadout.css";

function parseCssStyle(styleStr) {
  if (!styleStr) return {};
  const styleObj = {};
  styleStr.split(";").forEach((rule) => {
    const parts = rule.split(":");
    if (parts.length >= 2) {
      const prop = parts[0].trim();
      const val = parts.slice(1).join(":").trim();
      if (prop && val) {
        const camelProp = prop.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        styleObj[camelProp] = val;
      }
    }
  });
  return styleObj;
}

export function Username({ user, className = "", showBadge = false, asText = false, hover = true }) {
  if (!user) return null;
  const styleId = user?.loadout?.username_style || user?.username_style || "default";
  const style = USERNAME_STYLES.find((s) => s.id === styleId) || USERNAME_STYLES[0];
  
  let inlineStyle = {};
  if (styleId === "default") {
    if (user?.group_style) {
      inlineStyle = parseCssStyle(user.group_style);
    } else if (user?.group_color) {
      inlineStyle = { color: user.group_color };
    }
  }

  const nameText = user.username || "";
  const chars = nameText.split("");
  const total = chars.length;
  const center = (total - 1) / 2;

  const inner = styleId === "default" ? (
    <span 
      className="font-semibold" 
      style={inlineStyle} 
      data-testid={`username-${user.username}`} 
      data-text={user.username}
    >
      <span>{user.username}</span>
    </span>
  ) : (
    <span 
      className={`font-semibold splitting words chars ${style.className}`} 
      style={{
        "--word-total": 1,
        "--char-total": total,
        "--word-center": 0,
        "--char-center": center,
        ...inlineStyle
      }} 
      data-testid={`username-${user.username}`} 
      data-text={user.username}
    >
      <span className="word" style={{ "--word-index": 0, "--word-total": 1 }}>
        {chars.map((ch, idx) => (
          <span
            key={idx}
            className="char"
            data-char={ch}
            style={{
              "--char-index": idx,
              "--char-total": total,
              "--char-center": center,
              "--char-percent": total > 1 ? idx / (total - 1) : 0,
              "--char-offset": idx - center,
            }}
          >
            {ch}
          </span>
        ))}
      </span>
    </span>
  );
  const content = (
    <span className={`inline-flex items-center gap-1.5 notranslate ${className}`} translate="no">
      {asText ? inner : (
        <Link to={`/u/${user.username}`} className="hover:underline">
          {inner}
        </Link>
      )}
      {showBadge && ((user.is_vip) || (user.vip_tier && user.vip_tier !== "none") || (user.loadout?.banner_badge && user.loadout.banner_badge !== "none")) && (
        <VipBadge tier={user.loadout?.banner_badge || user.vip_tier} />
      )}
    </span>
  );
  if (!hover || !user.username) return content;
  return <MemberHoverCard user={user}>{content}</MemberHoverCard>;
}

export function VipBadge({ tier }) {
  if (!tier || tier === "none") return null;
  const badge = BANNER_BADGES.find((b) => b.id === tier || b.className?.includes(tier)) || BANNER_BADGES.find((b) => b.id === "goldPulseAnimated") || BANNER_BADGES[1];
  if (!badge || badge.id === "none") return null;
  return (
    <span className={`userBanner ${badge.className || "userBanner--orange"} notranslate`} translate="no" style={badge.color ? { backgroundColor: badge.color } : {}}>
      <span className="userBanner-before"></span>
      <strong>{badge.text || badge.label || "FORUMS VIP"}</strong>
      <span className="userBanner-after"></span>
    </span>
  );
}

export function Avatar({ user, size = 48, hover = false }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const frameId = user?.loadout?.avatar_frame || "none";
  const frame = AVATAR_FRAMES.find((f) => f.id === frameId) || AVATAR_FRAMES[0];
  const initial = (user?.username?.[0] || "?").toUpperCase();
  const innerSize = size - 6;
  const node = (
    <div
      className={`tf-frame ${frame.className || ""} inline-flex items-center justify-center tf-avatar-pop relative`}
      style={{ width: size, height: size, padding: frame.id === "none" || frame.imageUrl ? 0 : 3, borderRadius: 0 }}
      data-testid="avatar-frame"
    >
      {frame.imageUrl && (
        <img 
          src={frame.imageUrl} 
          alt="Frame" 
          loading="lazy" 
          decoding="async"
          className="absolute top-0 left-0 w-full h-full pointer-events-none z-10 scale-110" 
          style={{ borderRadius: 0 }}
        />
      )}
      <div
        className="bg-zinc-900 text-zinc-200 font-bold flex items-center justify-center w-full h-full overflow-hidden relative"
        style={{ fontFamily: "Rajdhani", fontSize: innerSize * 0.4, borderRadius: 0 }}
      >
        {user?.avatar_url ? (
          <>
            {!imgLoaded && (
              <span className="absolute inset-0 skeleton--default" style={{ borderRadius: 0 }} />
            )}
            <img 
              src={user.avatar_url} 
              alt={user.username} 
              loading="lazy" 
              decoding="async"
              onLoad={() => setImgLoaded(true)}
              className={`w-full h-full object-cover transition-opacity duration-300 ${imgLoaded ? "opacity-100" : "opacity-0"}`} 
              style={{ borderRadius: 0 }}
            />
          </>
        ) : (
          initial
        )}
      </div>
    </div>
  );
  if (!hover || !user?.username) return node;
  return <MemberHoverCard user={user}>{node}</MemberHoverCard>;
}
